import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyAppSubscriptionKey,
  ShopifyAppUsageRecordKey,
  ShopifyAppUsageRecordToAppSubscriptionKey,
  ShopifyAppUsageRecordToShopKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyAppUsageRecordKey,
  "2022-01": {
    name: "Shopify App Usage Record",
    fields: [
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt" } },
      },
      {
        name: "Description",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "description" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Price",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "price" } },
      },
      {
        name: "Subscription Line Item",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "subscriptionLineItem" } },
      },
      {
        name: "App Subscription",
        fieldType: FieldType.BelongsTo,
        key: ShopifyAppUsageRecordToAppSubscriptionKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "app_subscription_id" } },
        configuration: {
          relatedModelKey: ShopifyAppSubscriptionKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyAppUsageRecordToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "custom",
      resource: "appUsageRecord",
    },
  },
  "2025-04": {
    fields: [
      {
        name: "Idempotency Key",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "idempotencyKey" } },
      },
      {
        name: "Price",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "price",
            graphQLSelection: `
          amount
          currencyCode
          `,
          },
        },
      },
      {
        name: "Subscription Line Item",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "subscriptionLineItem",
            graphQLSelection: `
          id
          plan {
            pricingDetails {
              ... on AppRecurringPricing {
                discount {
                  durationLimitInIntervals
                  priceAfterDiscount {
                    amount
                    currencyCode
                  }
                  remainingDurationInIntervals
                  value
                }
                interval
                price {
                  amount
                  currencyCode
                }
              }
              ... on AppUsagePricing {
                balanceUsed {
                  amount
                  currencyCode
                }
                cappedAmount {
                  amount
                  currencyCode
                }
                interval
                terms
              }
            }
          }
          `,
          },
        },
      },
      {
        name: "App Subscription",
        fieldType: FieldType.BelongsTo,
        key: ShopifyAppUsageRecordToAppSubscriptionKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "app_subscription_id", skipGraphQLSelection: true } },
        configuration: {
          relatedModelKey: ShopifyAppSubscriptionKey,
        },
      },
    ],
    syncConfig: {
      type: "custom",
      resource: "appUsageRecordsQuery",
    },
  },
};

export const ShopifyAppUsageRecord = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
